import {
  ComponentPublicInstance,
  Plugin,
} from 'vue';
import { FirebaseCrashlytics, StackFrame } from '@capacitor-firebase/crashlytics';
import * as StackTrace from 'stacktrace-js';
import { Device } from '@capacitor/device';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/modules/profile/stores/user.ts';
import { Capacitor } from '@capacitor/core';

// https://capawesome.io/plugins/firebase/crashlytics/
// https://capacitorjs.com/docs/apis/device

const getUserId = () => {
  const { user } = storeToRefs(useUserStore());

  return user.value?.id || 'guest';
};

const setStaticKeysToReport = async () => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  const deviceInfo = await Device.getInfo();

  await FirebaseCrashlytics.setCustomKey({
    key: 'webViewVersion',
    value: deviceInfo.webViewVersion || 'unknown',
    type: 'string',
  });
  await FirebaseCrashlytics.setCustomKey({
    key: 'isOnEmulator',
    value: deviceInfo.isVirtual,
    type: 'boolean',
  });
  await FirebaseCrashlytics.setCustomKey({
    key: 'androidSDKVersion',
    value: deviceInfo.androidSDKVersion || 0,
    type: 'float',
  });
  await FirebaseCrashlytics.setCustomKey({
    key: 'iOSVersion',
    value: deviceInfo.iOSVersion || 0,
    type: 'float',
  });
  await FirebaseCrashlytics.setCustomKey({
    key: 'osVersion',
    value: deviceInfo.osVersion,
    type: 'string',
  });
};

export const sendNonFatalError = async ({ message, info, stacktrace }: { message: string, info?: string, stacktrace?: StackFrame[] }) => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  const deviceInfo = await Device.getInfo();

  await FirebaseCrashlytics.setUserId({
    userId: getUserId(),
  });

  // send non-fatal event to Firebase Crashlytics
  return await FirebaseCrashlytics.recordException({
    message: `Error: ${message}, Info: ${info}`,
    keysAndValues: [
      {
        key: 'memoryUsedByTheApplication_(MBs)', // Approximate memory used by the current app, in Megabytes
        value: (deviceInfo.memUsed || 0) / 1048576,
        type: 'float',
      },
    ],
    stacktrace,
  });
};

export const sendFatalError = async (error: Error, info: string) => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  await FirebaseCrashlytics.setUserId({
    userId: getUserId(),
  });

  // send fatal event to Firebase Crashlytics and close app
  await FirebaseCrashlytics.crash({
    message: `Error: ${(error as Error).message}, Info: ${info}`,
  });
};

export const globalSyncErrorHandler = async (
  err: unknown,
  instance: ComponentPublicInstance | null,
  info: string,
) => {
  // err: error object
  // instance: the component instance in which the error occurred
  // info: a string indicating the type of error source (e.g. "render function", "created hook")
  const stacktrace = await StackTrace.fromError(err as Error);

  console.log('globalSyncErrorHandler error:', (err as Error).message);

  await sendNonFatalError({
    message: (err as Error).message,
    info,
    stacktrace,
  });
};

export const globalAsyncErrorHandler = () => async (event: PromiseRejectionEvent) => {
  console.log('globalAsyncErrorHandler error:', event.reason.message);

  const stacktrace = await StackTrace.fromError(event.reason);

  await sendNonFatalError({
    message: (event.reason as Error).message,
    info: event.reason,
    stacktrace,
  });
};

export const sendLog = async (message: string) => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  await FirebaseCrashlytics.setUserId({
    userId: getUserId(),
  });

  await FirebaseCrashlytics.log({
    message,
  });
};

const crash = async () => {
  await FirebaseCrashlytics.crash({
    message: 'Test Crash',
  });
};

export const FirebaseCrashlyticsPlugin = {
  async install() {
    await setStaticKeysToReport();

    // crash();
  },
} as Plugin;
